import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShipperOptionsQuery } from 'src/hooks/useShipperOptions.query';

type AnalyticsAction = 'click' | 'close' | 'print';

type Params = {
  /** 帳票対象の開始日 (YYYY-MM-DD) */
  startOn: string;
  /** 帳票対象の終了日 (YYYY-MM-DD) */
  endOn: string;
  /**
   * 帳票対象の shift_id 群。
   * 0 件の場合 (= 選択なし) は呼び出し側で「全件」相当の shift_id 配列を渡す前提。
   */
  shiftIds: number[];
  /**
   * 帳票機能の操作トラッキング用コールバック。任意。
   *   - 'click': 出力ボタン押下 (selectPrint ダイアログを開いた)
   *   - 'close': selectPrint ダイアログを閉じた
   *   - 'print': 各帳票ボタン押下 (buttonName で帳票種を渡す)
   * 内部で例外が発生しても帳票機能は継続する。
   */
  onAnalyticsEvent?: (action: AnalyticsAction, buttonName: string) => void;
};

export const useReportPrinting = ({ startOn, endOn, shiftIds, onAnalyticsEvent }: Params) => {
  const [selectPrintDialogOpen, setSelectPrintDialogOpen] = useState(false);
  const [directionSettingDialogOpen, setDirectionSettingDialogOpen] = useState(false);
  const [shipperDialogOpen, setShipperDialogOpen] = useState(false);
  const [shipperDialogMode, setShipperDialogMode] = useState<'delivery-by-course' | 'picking-list' | null>(null);

  const onAnalyticsEventRef = useRef(onAnalyticsEvent);
  useEffect(() => {
    onAnalyticsEventRef.current = onAnalyticsEvent;
  }, [onAnalyticsEvent]);
  const emit = useCallback((action: AnalyticsAction, buttonName: string) => {
    try {
      onAnalyticsEventRef.current?.(action, buttonName);
    } catch {
      // GA 障害でユーザ操作を止めない
    }
  }, []);

  const idsCsv = useMemo(() => shiftIds.join(','), [shiftIds]);

  const open = useCallback(() => {
    emit('click', '出力');
    setSelectPrintDialogOpen(true);
  }, [emit]);
  const closeSelectPrint = useCallback(() => {
    emit('close', '出力');
    setSelectPrintDialogOpen(false);
  }, [emit]);
  const closeDirectionSetting = useCallback(() => setDirectionSettingDialogOpen(false), []);

  const printAllOperationDirectionsOnClick = useCallback(() => {
    emit('print', '配送指示書');
    setSelectPrintDialogOpen(false);
    setDirectionSettingDialogOpen(true);
  }, [emit]);
  const printOperationDirectionUrl = useCallback(
    () => `/operation-directions/?ids=${idsCsv}&variant=v3`,
    [idsCsv],
  );

  const printAllTruckDirectionsOnClick = useCallback(() => {
    emit('print', 'すべての配車表');
    setSelectPrintDialogOpen(false);
    window.open(`/truck-directions/?ids=${idsCsv}`);
  }, [idsCsv, emit]);
  const printUnloadTruckDirectionsOnClick = useCallback(() => {
    emit('print', '納品のみの配車表');
    setSelectPrintDialogOpen(false);
    window.open(`/truck-directions/?ids=${idsCsv}&action=降`);
  }, [idsCsv, emit]);
  const printPickingListOnClick = useCallback(() => {
    emit('print', 'ピッキングリスト');
    setSelectPrintDialogOpen(false);
    setShipperDialogMode('picking-list');
    setShipperDialogOpen(true);
  }, [emit]);
  const printCourseDeliveryCountReportOnClick = useCallback(() => {
    emit('print', 'コース別配送件数リスト');
    setSelectPrintDialogOpen(false);
    const query = new URLSearchParams({ start_on: startOn, end_on: endOn });
    window.open(`/course-delivery-counts?${query.toString()}`);
  }, [startOn, endOn, emit]);
  const printDeliveryByCourseReportOnClick = useCallback(() => {
    emit('print', '納品先別コースリスト');
    setSelectPrintDialogOpen(false);
    setShipperDialogMode('delivery-by-course');
    setShipperDialogOpen(true);
  }, [emit]);
  const downloadPlanCsvPerPacketOnClick = useCallback(() => {
    emit('print', '配車CSV');
    setSelectPrintDialogOpen(false);
    window.open(`/api/v3/shifts/csv?start_at_range_start_on=${startOn}&start_at_range_end_on=${endOn}&summary_type=packet`);
  }, [startOn, endOn, emit]);
  const downloadPlanCsvPerPlaceOnClick = useCallback(() => {
    emit('print', '配車XLSX');
    setSelectPrintDialogOpen(false);
    window.open(`/api/v3/shifts/excel?start_at_range_start_on=${startOn}&start_at_range_end_on=${endOn}&summary_type=place`);
  }, [startOn, endOn, emit]);

  const {
    data: shipperOptions,
    isLoading: shipperOptionsIsLoading,
    isError: shipperOptionsIsError,
  } = useShipperOptionsQuery({ startOn, endOn, enabled: shipperDialogOpen });
  const handleShipperDialogBack = useCallback(() => {
    setShipperDialogOpen(false);
    setShipperDialogMode(null);
    setSelectPrintDialogOpen(true);
  }, []);
  const handleShipperDialogConfirm = useCallback((selected: string[], barcodeSelected: string[] = []) => {
    setShipperDialogOpen(false);
    if (shipperDialogMode === 'picking-list') {
      const query = new URLSearchParams({ ids: idsCsv, date: startOn });
      selected.forEach((name) => query.append('shipper_names[]', name));
      barcodeSelected.forEach((name) => query.append('barcode_shipper_names[]', name));
      window.open(`/picking-list/?${query.toString()}`);
    } else {
      const query = new URLSearchParams();
      query.set('start_on', startOn);
      query.set('end_on', endOn);
      selected.forEach((name) => query.append('shipper_names[]', name));
      window.open(`/deliveries-by-course?${query.toString()}`);
    }
    setShipperDialogMode(null);
  }, [idsCsv, shipperDialogMode, startOn, endOn]);

  return {
    open,
    dialogProps: {
      selectPrintDialogOpen,
      onCloseSelectPrint: closeSelectPrint,
      directionSettingDialogOpen,
      onCloseDirectionSetting: closeDirectionSetting,
      printOperationDirectionUrl,
      shipperDialogOpen,
      shipperDialogMode,
      shipperOptionsNames: shipperOptions?.names ?? [],
      shipperOptionsIsLoading,
      shipperOptionsIsError,
      onShipperDialogBack: handleShipperDialogBack,
      onShipperDialogConfirm: handleShipperDialogConfirm,
      printAllOperationDirectionsOnClick,
      printAllTruckDirectionsOnClick,
      printUnloadTruckDirectionsOnClick,
      printPickingListOnClick,
      printCourseDeliveryCountReportOnClick,
      printDeliveryByCourseReportOnClick,
      downloadPlanCsvPerPacketOnClick,
      downloadPlanCsvPerPlaceOnClick,
    },
  } as const;
};

export type ReportPrintingDialogProps = ReturnType<typeof useReportPrinting>['dialogProps'];
